import { Alert, Col, Form, InputGroup } from "react-bootstrap";
import { IBookingPassenger, useStateSelector } from "../Models";
import React, { useEffect, useState } from "react";
import { getStrings } from "../../Shared/Reservation/StringResources";
import SvgPersonFill from "../../Shared/Icons/bootstrap/PersonFill";
import SvgPhone from "../../Shared/Icons/bootstrap/Phone";
import { useFormContext } from "react-hook-form";
import { isValidBirthDate, isValidEmail, isValidTelefonnummer, validationCssClass } from "../../Shared/Validation";
import { IPassengerAndPaymentFields } from "./PassengersAndPayment";
import { Countries, ICountryDefinition, Sweden } from "../../../shared/Countries";
import { run as runMailcheck } from "mailcheck";
import SvgCalendar from "../../Shared/Icons/bootstrap/Calendar";
import { CountrySelector } from "../../Shared/Components/CountrySelector";
import Icon from "@mdi/react";
import { mdiSnowflakeVariant } from "@mdi/js";

const strings = getStrings().component.orderDetails.passengerDetails;

export const PassengerDetails = (props: { index: number; passenger: IBookingPassenger; requireBirthDateAndCountry: boolean; showVasaId: boolean }) => {

	const passengerTypes = useStateSelector(state => state.search.passengerTypes);
	const desc = passengerTypes.find(x => x.code === props.passenger.type)?.description || props.passenger.type;
	const idx = props.index;
	const isCustomer = props.index === 0;

	const { register, formState, watch, setValue, trigger } = useFormContext<IPassengerAndPaymentFields>();
	const watchPhone = watch(`passengers.${idx}.phoneNumber`);
	const watchEmail = watch(`passengers.${idx}.email`);

	const [phoneCountry, setPhoneCountry] = useState<ICountryDefinition>(Sweden);
	const [emailSuggestion, setEmailSuggestion] = useState<MailcheckModule.ISuggestion>();

	//Försök matcha land när du fyller i telefon
	useEffect(() => {
		if (props.passenger.type === "PET")
			return;

		if (watchPhone?.startsWith("+") && watchPhone?.length >= 2) {
			const match = Countries.find(x =>
				x.dial_code === watchPhone.substring(0, 2) ||
				x.dial_code === watchPhone.substring(0, 3) ||
				x.dial_code === watchPhone.substring(0, 4));
			if (match) {
				setPhoneCountry(match);
				setValue(`passengers.${idx}.phoneCountry`, match.code);
			}
			else {
				setValue(`passengers.${idx}.phoneCountry`, undefined);
			}
		}
		else
		{
			setPhoneCountry(Sweden);
			setValue(`passengers.${idx}.phoneCountry`, "SE");
		}

	}, [watchPhone]);

	if (props.passenger.type === "PET")
		return null;

	const checkEmail = () => {
		if (!isValidEmail(watchEmail))
		{
			setEmailSuggestion(undefined);
			return;
		}

		runMailcheck({
			email: watchEmail,
			suggested: (suggestion: MailcheckModule.ISuggestion) => setEmailSuggestion(suggestion),
			empty: () => setEmailSuggestion(undefined)
		});
	};

	const countrySelected = (country: string, countryCode: string) => {
		console.debug("countrySelected", country, countryCode);
		setValue(`passengers.${idx}.countryOfResidence`, countryCode);
		trigger(`passengers.${idx}.countryOfResidence`);
	}

	return <>
		<Form.Control type="hidden" {...register(`passengers.${idx}.id`, { value: props.passenger.id })} />
		<Form.Control type="hidden" {...register(`passengers.${idx}.type`, { value: props.passenger.type })} />
		<fieldset>
			<legend>{strings.traveller} {props.index+1} ({desc})</legend>
		</fieldset>
		<Form.Row>
			<Form.Group as={Col} xs={12} sm={6} controlId={`passengers.${idx}.firstname`}>
				<Form.Label>{strings.firstnameLabel}</Form.Label>
				<InputGroup>
					<InputGroup.Prepend><InputGroup.Text><SvgPersonFill /></InputGroup.Text></InputGroup.Prepend>
					<Form.Control type="text" {...register(`passengers.${idx}.firstname`, { required: true }) }
						className={validationCssClass(formState, `passengers.${idx}.firstname`)} />
				</InputGroup>
			</Form.Group>
			<Form.Group as={Col} xs={12} sm={6} controlId={`passengers.${idx}.lastname`}>
				<Form.Label>{strings.lastnameLabel}</Form.Label>
				{/* <InputGroup>
					<InputGroup.Prepend><InputGroup.Text><SvgPersonFill /></InputGroup.Text></InputGroup.Prepend> */}
					<Form.Control type="text" {...register(`passengers.${idx}.lastname`, { required: true }) }
						className={validationCssClass(formState, `passengers.${idx}.lastname`)} />
				{/* </InputGroup> */}
			</Form.Group>
		</Form.Row>

		{ isCustomer && <>
			<Form.Row>
				<Form.Group as={Col} xs={12} sm={6} controlId={`passengers.${idx}.phoneNumber`}>
					<Form.Label>{strings.phoneLabel}</Form.Label>
					<InputGroup className="phone-input">
						<InputGroup.Prepend>
							{phoneCountry?.code && <div className="country"><img src={`/flags/${phoneCountry.code.toLowerCase()}.svg`} alt={phoneCountry.name} /></div> }
							<InputGroup.Text><SvgPhone /></InputGroup.Text>
						</InputGroup.Prepend>
						<Form.Control type="tel" autoComplete="tel" placeholder="+46XXXXXXXXX"
							{...register(`passengers.${idx}.phoneNumber`, { validate: x => isValidTelefonnummer(x, false) })}
							className={validationCssClass(formState, `passengers.${idx}.phoneNumber`)} />
					</InputGroup>
					{ strings.phoneHint && <div className="small">{strings.phoneHint}</div> }
				</Form.Group>
				<Form.Control type="hidden" {...register(`passengers.${idx}.phoneCountry`)} />
				<Form.Group as={Col} xs={12} sm={6} controlId={`passengers.${idx}.email`}>
					<Form.Label>{strings.emailLabel}</Form.Label>
					<InputGroup onBlur={checkEmail}>
						<InputGroup.Prepend><InputGroup.Text>@</InputGroup.Text></InputGroup.Prepend>
						<Form.Control type="email" autoComplete="email"
							{...register(`passengers.${idx}.email`, { validate: x => isValidEmail(x, false) }) }
							className={validationCssClass(formState, `passengers.${idx}.email`)} />
					</InputGroup>
					{ emailSuggestion && <div className="small mt-1">{strings.emailSuggestion} <button type="button" className="link-button" onClick={() => setValue(`passengers.${idx}.email`, emailSuggestion.full)}>{emailSuggestion.full}</button>?</div>}
				</Form.Group>
			</Form.Row>
		</>}

		{props.requireBirthDateAndCountry && <Form.Row>
			<Form.Group as={Col} xs={12} sm={6} controlId={`passengers.${idx}.birthDate`}>
				<Form.Label>{strings.birthdateLabel}</Form.Label>
				<InputGroup>
					<InputGroup.Prepend><InputGroup.Text><SvgCalendar /></InputGroup.Text></InputGroup.Prepend>
					<Form.Control type="date" {...register(`passengers.${idx}.birthDate`, { required: true, validate: x => isValidBirthDate(x) }) }
						className={validationCssClass(formState, `passengers.${idx}.birthDate`)} />
				</InputGroup>
				{ formState.errors[`passengers.${idx}.birthDate`] && <Form.Text className="text-danger">{strings.birthdateError}</Form.Text>}
			</Form.Group>

			<Form.Group as={Col} xs={12} sm={6}>
				<Form.Control type="hidden" {...register(`passengers.${idx}.countryOfResidence`, { required: true })} />
				<Form.Label>{strings.countryLabel}</Form.Label>
				<CountrySelector onCountrySelected={countrySelected} required />
				<Form.Text muted>{strings.countryHint}</Form.Text>
			</Form.Group>

		</Form.Row>}

		{props.showVasaId && <>
			{ idx === 0 && <Alert variant="info">{strings.vasaIdInfo}</Alert>}
			<Form.Row>
				<Form.Group as={Col} xs={12} sm={6} controlId={`passengers.${idx}.vasaid`}>
					<Form.Label>Vasa ID</Form.Label>
					<InputGroup>
						<InputGroup.Prepend><InputGroup.Text><Icon path={mdiSnowflakeVariant} /></InputGroup.Text></InputGroup.Prepend>
						<Form.Control type="text" {...register(`passengers.${idx}.vasaid`, { required: false }) }
							className={validationCssClass(formState, `passengers.${idx}.vasaid`)} />
					</InputGroup>
				</Form.Group>
			</Form.Row>
		</>}
	</>
};